import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import './ApproveRejectModal.css';

interface ApproveRejectModalProps {
  show: boolean;
  handleClose: () => void;
  handleConfirm: (note: string) => void;
  actionType: 'approve' | 'reject';
  isLoading?: boolean;
  context?: 'return' | 'cancellation'; // Context for modal text
}

const ApproveRejectModal: React.FC<ApproveRejectModalProps> = ({
  show,
  handleClose,
  handleConfirm,
  actionType,
  isLoading = false,
  context = 'return'
}) => {
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset note and validation error when modal opens/closes
  useEffect(() => {
    if (!show) {
      setNote('');
      setValidationError('');
    }
  }, [show]);

  const handleSubmit = () => {
    // For reject action, note is required
    if (actionType === 'reject' && !note.trim()) {
      setValidationError('Note is required for rejection');
      return;
    }
    
    setValidationError('');
    handleConfirm(note);
    setNote(''); // Reset note after submission
  };

  const handleModalClose = () => {
    if (!isLoading) {
      setNote('');
      setValidationError('');
      handleClose();
    }
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
    // Clear validation error when user starts typing
    if (validationError && e.target.value.trim()) {
      setValidationError('');
    }
  };

  const getTitle = () => {
    if (context === 'cancellation') {
      return actionType === 'approve' ? 'Approve Cancellation?' : 'Reject Cancellation?';
    }
    return actionType === 'approve' ? 'Approve Return?' : 'Reject Return?';
  };

  const getMessage = () => {
    if (context === 'cancellation') {
      return actionType === 'approve' 
        ? 'Are you sure you want to approve this cancellation request?'
        : 'Are you sure you want to reject this cancellation request?';
    }
    return actionType === 'approve' 
      ? 'Are you sure you want to approve this return request?'
      : 'Are you sure you want to reject this return request?';
  };

  const getConfirmButtonText = () => {
    return actionType === 'approve' ? 'Approve' : 'Reject';
  };

  return (
    <Modal 
      show={show} 
      onHide={handleModalClose}
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>{getTitle()}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <p className="approve-reject-modal-message">{getMessage()}</p>
        
        <Form.Group className="mb-3">
          <Form.Label>
            Note {actionType === 'reject' ? '(Required)' : '(Optional)'}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={`Reason for ${actionType === 'approve' ? 'approval' : 'rejection'}...`}
            value={note}
            onChange={handleNoteChange}
            disabled={isLoading}
            isInvalid={!!validationError}
          />
          {validationError && (
            <Form.Control.Feedback type="invalid">
              {validationError}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleModalClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          variant={actionType === 'approve' ? 'primary' : 'danger'}
          onClick={handleSubmit}
          disabled={isLoading || (actionType === 'reject' && !note.trim())}
        >
          {isLoading ? 'Processing...' : getConfirmButtonText()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApproveRejectModal;
